import { motion } from "framer-motion";
import { useLang } from "@/lib/LanguageContext";
import { Eyebrow, Reveal } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

const EASE = [0.22, 1, 0.36, 1];

export default function ExecutionData() {
  const { c } = useLang();
  const e = c.execution;
  return (
    <section id="execution" data-testid="execution-section" className="relative py-28 sm:py-40 bg-[#E7E2D1] border-b border-forest/10 overflow-hidden">
      <LeafDecor layers={[
        { img: "side", className: "bottom-0 left-0 h-[600px] -ml-28 -mb-8 -rotate-12", opacity: 0.3 },
        { img: "corner", className: "top-0 right-0 w-[440px] -mt-16 -mr-20 rotate-[80deg]", opacity: 0.32 },
        { img: "bunch", className: "bottom-0 right-1/4 w-[240px] -mb-6 rotate-6 hidden lg:block", opacity: 0.2 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <Eyebrow testid="execution-eyebrow">{e.eyebrow}</Eyebrow>
        <h2 className="mt-6 font-serif font-light leading-none tracking-[-0.02em] text-forest text-5xl sm:text-6xl lg:text-7xl">
          {e.heading.pre}<span className="text-gold italic">{e.heading.em}</span>
        </h2>

        <div className="mt-16 grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <div className="font-sans text-xs uppercase tracking-[0.24em] text-gold mb-6">{e.logisticsLabel}</div>
            <div className="border-t border-forest/15">
              {e.logistics.map((row, i) => (
                <motion.div
                  key={i}
                  data-testid={`logistics-row-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                  className="group flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 py-4 sm:py-5 border-b border-forest/10 hover:bg-forest/[0.04] transition-colors duration-400 px-2"
                >
                  <span className="font-sans text-[11px] sm:text-xs uppercase tracking-[0.16em] text-forest/90">{row.k}</span>
                  <span className="font-serif text-lg sm:text-2xl text-forest sm:text-right">{row.v}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <div className="font-sans text-xs uppercase tracking-[0.24em] text-gold mb-6">{e.disciplineLabel}</div>
            <ul className="space-y-4">
              {e.discipline.map((d, i) => (
                <Reveal key={i} delay={i * 0.05} as="li">
                  <div className="flex gap-4 items-start">
                    <span className="mt-2 rotate-45 inline-block h-1.5 w-1.5 bg-leaf shrink-0" />
                    <p className="font-sans text-sm leading-relaxed text-forest/95">{d}</p>
                  </div>
                </Reveal>
              ))}
            </ul>

            <Reveal delay={0.15} className="mt-10 border border-forest/15 p-7 bg-white shadow-[0_16px_40px_-28px_rgba(3,22,12,0.4)]">
              <div className="font-sans text-xs uppercase tracking-[0.2em] text-gold mb-3">{e.riskLabel}</div>
              <p className="font-sans text-sm leading-relaxed text-forest/95">{e.risk}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
